import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { People } from "@/api/endpoints";
import type { Deal } from "@/api/types";
import { agoLabel } from "@/lib/people";
import { Avatar } from "@/components/ui";
import ContactsSection from "@/components/ContactsSection";
import Warmth from "./Warmth";

const ROLE_LABEL: Record<string, string> = {
  primary: "Owner",
  owner: "Co-owner",
  viewer: "Watching",
  contributor: "Took part",
};

/** Who we know on this deal, how warm it is, and who on our side knows them. */
export default function PeoplePanel({ deal }: { deal: Deal }) {
  const [managing, setManaging] = useState(false);
  const peopleQuery = useQuery({
    queryKey: ["deal-people", deal.id],
    queryFn: () => People.forDeal(deal.id),
  });
  const contacts = peopleQuery.data?.contacts ?? [];
  const theirs = contacts.filter((c) => c.kind === "account_contact");
  const brokers = contacts.filter((c) => c.kind === "broker_contact");
  const team = peopleQuery.data?.team ?? [];

  if (managing) {
    return (
      <div className="space-y-3">
        <button className="btn btn-ghost !px-2 text-sm" onClick={() => setManaging(false)}>
          ← Back to people
        </button>
        <ContactsSection dealId={deal.id} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section>
        <h4 className="section-title mb-2">Their side</h4>
        {peopleQuery.isLoading ? (
          <p className="text-sm text-mut-500">Loading…</p>
        ) : theirs.length === 0 ? (
          <p className="text-sm text-mut-500">No contacts on this deal yet.</p>
        ) : (
          <ul className="space-y-3">
            {theirs.map((c) => (
              <li key={c.contact_id} className="flex gap-2.5">
                <Avatar name={c.name} size={26} muted />
                <div className="min-w-0 flex-1">
                  <div className="text-md font-medium truncate">{c.name}</div>
                  <div className="text-xs text-mut-500 truncate">
                    {[c.role, c.title].filter(Boolean).join(" · ") || "No role set"}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <Warmth lastTouch={c.last_touch} />
                    <span className="text-2xs text-mut-500">{agoLabel(c.last_touch)}</span>
                  </div>
                  {c.known_by.length > 0 && (
                    <div className="text-2xs text-mut-500 mt-0.5 truncate">
                      Knows {c.known_by.join(", ")}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h4 className="section-title mb-2">Our side</h4>
        <ul className="space-y-2">
          {team.map((m) => (
            <li key={m.name} className="flex items-center gap-2.5">
              <Avatar name={m.name} size={24} />
              <div className="min-w-0 flex-1">
                <div className="text-md font-medium truncate">{m.name}</div>
                <div className="text-2xs text-mut-500">
                  {ROLE_LABEL[m.role] ?? m.role}
                  {m.touches > 0 &&
                    ` · ${m.touches} touch${m.touches === 1 ? "" : "es"} in 90 days`}
                </div>
              </div>
            </li>
          ))}
          {team.length === 0 && <li className="text-sm text-mut-500">Nobody yet.</li>}
        </ul>
      </section>

      {(deal.sourcer_kind === "contact" || brokers.length > 0) && (
        <section>
          <h4 className="section-title mb-2">Introduced by</h4>
          <ul className="space-y-2">
            {deal.sourcer_kind === "contact" &&
              deal.sourcer_name &&
              !brokers.some((b) => b.name === deal.sourcer_name) && (
                <li className="flex items-center gap-2.5">
                  <Avatar name={deal.sourcer_name} size={24} role="amber" />
                  <div className="text-md font-medium">{deal.sourcer_name}</div>
                </li>
              )}
            {brokers.map((b) => (
              <li key={b.contact_id} className="flex items-center gap-2.5">
                <Avatar name={b.name} size={24} role="amber" />
                <div className="min-w-0">
                  <div className="text-md font-medium truncate">{b.name}</div>
                  <div className="text-2xs text-mut-500">
                    Broker · last in touch {agoLabel(b.last_touch)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <button className="btn w-full" onClick={() => setManaging(true)}>
        Add or edit contacts
      </button>
    </div>
  );
}
